// Master-P3 Wave 7.5 — Autopilot safety controls.
//
// Rendered at the top of `/app/autopilot` and exposes:
//   1. Emergency-Stop button (red, confirm-dialog gated).
//   2. Hard-Limits card (read-only, "[technisch fixiert]" chip — the
//      values come from a hardcoded backend constants dict, no PUT
//      endpoint exists).
//   3. Business-Frame current-active-view + link to a version list.
import React, { useEffect, useState } from "react";
import { api } from "../../lib/api";
import { toast } from "sonner";
import {
  Card, CardContent, CardHeader, CardTitle, CardDescription,
} from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
  DialogFooter, DialogDescription,
} from "../../components/ui/dialog";
import { AlertOctagon, Lock, Scale } from "lucide-react";


const HARD_LIMITS = {
  max_single_action_spend_change_pct: 200,
  max_actions_per_run_per_tenant: 20,
  min_seconds_between_actions_same_campaign: 300,
  max_pending_actions_per_tenant: 50,
  emergency_stop_grace_seconds: 0,
};


function EmergencyStopButton() {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const run = async () => {
    setBusy(true);
    try {
      const r = await api.post("/autopilot/emergency-stop", {});
      setResult(r.data);
      toast.success(
        `Not-Aus: ${r.data.cancelled} storniert, ` +
        `${r.data.dispatching_to_unknown} als unknown markiert.`);
    } catch (e) {
      toast.error("Not-Aus fehlgeschlagen: " + e.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <Button variant="destructive"
              onClick={() => setOpen(true)}
              data-testid="autopilot-emergency-stop-btn">
        <AlertOctagon className="h-4 w-4 mr-2" />
        Not-Aus
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="autopilot-emergency-stop-dialog">
          <DialogHeader>
            <DialogTitle>Not-Aus für alle Autopilot-Aktionen</DialogTitle>
            <DialogDescription>
              Stoppt sofort alle offenen Autopilot-Aktionen dieses
              Tenants. Bereits dispatched Aktionen bleiben unbekannt
              — kein Anbieter-Rollback wird ausgelöst.
            </DialogDescription>
          </DialogHeader>
          {result && (
            <pre className="text-xs bg-muted p-2 rounded"
                 data-testid="autopilot-emergency-stop-result">
{JSON.stringify(result, null, 2)}
            </pre>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}
                    data-testid="autopilot-emergency-stop-cancel">
              Abbrechen
            </Button>
            <Button variant="destructive"
                    onClick={run} disabled={busy}
                    data-testid="autopilot-emergency-stop-confirm">
              {busy ? "Wird ausgeführt..." : "Bestätigt — Not-Aus"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}


function HardLimitsCard() {
  return (
    <Card data-testid="autopilot-hard-limits-card">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-base">
          <Lock className="h-4 w-4" /> Technische Schutzgrenzen
          <Badge className="bg-red-100 text-red-800 border-0 ml-2"
                 data-testid="autopilot-hard-limits-badge">
            technisch fixiert
          </Badge>
        </CardTitle>
        <CardDescription>
          Nicht per UI editierbar. Änderungen erfordern Code-Review
          und Platform-Owner-Freigabe.
        </CardDescription>
      </CardHeader>
      <CardContent>
        <table className="w-full text-sm"
               data-testid="autopilot-hard-limits-table">
          <tbody>
            {Object.entries(HARD_LIMITS).map(([k, v]) => (
              <tr key={k} className="border-t"
                  data-testid={`hard-limit-row-${k}`}>
                <td className="p-2 font-mono text-xs">{k}</td>
                <td className="p-2 text-right font-medium">{v}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </CardContent>
    </Card>
  );
}


function BusinessFrameCard() {
  const [state, setState] = useState({ loading: true, frame: null });

  useEffect(() => {
    api.get("/autopilot/business-frame/active")
      .then((r) => setState({ loading: false,
                              frame: r.data.frame || null }))
      .catch(() => setState({ loading: false, frame: null }));
  }, []);

  return (
    <Card data-testid="autopilot-business-frame-card">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-base">
          <Scale className="h-4 w-4" /> Business-Rahmen (versioniert)
          {state.frame && (
            <Badge className="bg-emerald-100 text-emerald-800 border-0 ml-2"
                   data-testid="autopilot-business-frame-version-badge">
              v{state.frame.version}
            </Badge>
          )}
        </CardTitle>
        <CardDescription>
          Vom Betreiber editierbar. Jede Änderung erzeugt eine neue
          Version; die vorherige bleibt lesbar (kein Hard-Delete).
        </CardDescription>
      </CardHeader>
      <CardContent data-testid="autopilot-business-frame-content">
        {state.loading && <p className="text-sm">Wird geladen…</p>}
        {!state.loading && !state.frame && (
          <p className="text-sm text-muted-foreground"
             data-testid="autopilot-business-frame-empty">
            Kein aktiver Business-Rahmen. Über
            <code className="mx-1">POST /autopilot/business-frame</code>
            einen anlegen.
          </p>
        )}
        {state.frame && (
          <div className="text-sm space-y-1"
               data-testid="autopilot-business-frame-active">
            <div>Name: <b>{state.frame.name}</b></div>
            <div>Max. tägliche Änderung:{" "}
              {state.frame.max_daily_change_pct}%</div>
            <div>Max. tägliche Ausgabe:{" "}
              €{state.frame.max_daily_spend_eur}</div>
            <div>Erlaubte Aktionen:{" "}
              {(state.frame.allowed_action_types || []).join(", ")
                || "—"}</div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}


export default function AutopilotSafetyControls() {
  return (
    <div className="space-y-4" data-testid="autopilot-safety-controls">
      <div className="flex items-center justify-between gap-4
                      flex-wrap">
        <div>
          <h2 className="text-lg font-semibold">Sicherheits-Kontrollen</h2>
          <p className="text-sm text-muted-foreground">
            Technische Schutzgrenzen + Business-Rahmen + Not-Aus.
          </p>
        </div>
        <EmergencyStopButton />
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <HardLimitsCard />
        <BusinessFrameCard />
      </div>
    </div>
  );
}
